import { useEffect, useState } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { FiMic, FiMicOff, FiChevronUp, FiChevronDown, FiVideo } from "react-icons/fi";
import { practice, usePractice } from "../../lib/practice/session";
import { canListen } from "../../lib/practice/listener";
// The dialogs and camera load when first needed, so every page stays light.
const SessionSummary = dynamic(() => import("./SessionSummary"), { ssr: false });
const Calibration = dynamic(() => import("./Calibration"), { ssr: false });
const CameraTakes = dynamic(() => import("./CameraTakes"), { ssr: false });
import { ItemList, Meter } from "./parts";
import styles from "./sessions.module.css";

const clock = (ms) => { const seconds = Math.max(0, Math.floor(ms / 1000)); return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`; };

// Follows you around the app while a practice session runs: the time, what's playing, the
// microphone with live accuracy, and Finish. Opens the summary once a session is finished.
export default function SessionBar() {
  const session = usePractice(), [now, setNow] = useState(Date.now()), [open, setOpen] = useState(false), [calibrating, setCalibrating] = useState(false), [camera, setCamera] = useState(false);
  useEffect(() => { if (!session.active) return; const timer = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(timer); }, [session.active]);
  if (!session.active) return <>
    {session.finished && <SessionSummary session={session.finished} clips={session.clips} onClose={() => practice.clearFinished()} />}
  </>;
  const listening = session.mic === "on", playing = session.playing;
  const accuracy = playing ? session.live?.accuracy : session.lastTake?.accuracy;
  return <div className={styles.bar} role="region" aria-label="Practice session">
    {open && <div className={styles.panel}>
      <h2>This session</h2>
      {session.items.length ? <ItemList items={session.items} /> : <p>Play a rhythm in Score, the Vault or Reels and it counts here.</p>}
      {session.mic === "error" && <p className={styles.error}>{session.micError}</p>}
      {listening && !session.calibration && <p>Calibrate once so timing is measured exactly for your device.</p>}
      {listening && session.calibration?.speakersTooLoud && <p>The microphone hears your speakers loudly. Headphones give more accurate scores.</p>}
      <div className={styles.panelActions}>
        {canListen() && <button onClick={() => setCalibrating(true)}>{session.calibration ? "Recalibrate" : "Calibrate"} microphone</button>}
        {typeof window.MediaRecorder !== "undefined" && !camera && <button onClick={() => { setCamera(true); setOpen(false); }}><FiVideo aria-hidden="true" /> Record my takes</button>}
        <Link href="/practice">Practice page</Link>
        <button onClick={() => { if (window.confirm("Discard this practice session?")) { practice.discard(); setOpen(false); } }}>Discard session</button>
      </div>
    </div>}
    {camera && <CameraTakes onClose={() => setCamera(false)} />}
    <div className={styles.pill}>
      <span className={styles.timer}><span className={styles.dot} aria-hidden="true" />{clock(now - session.startedAt)}</span>
      <button className={styles.now} onClick={() => setOpen(!open)} aria-expanded={open} aria-label={open ? "Hide session details" : "Show session details"}>
        {playing ? <><strong>{playing.name}</strong> · {playing.tempo} BPM</> : session.lastTake ? <>Last: <strong>{session.lastTake.name}</strong></> : "Play any rhythm"}
        {" "}{open ? <FiChevronDown aria-hidden="true" /> : <FiChevronUp aria-hidden="true" />}
      </button>
      {canListen() && <button className={styles.listen} aria-pressed={listening} onClick={() => practice.listen(!listening)} disabled={session.mic === "starting"}
        title={listening ? "Stop listening" : "Listen to score your timing"}>
        {listening ? <FiMic aria-hidden="true" /> : <FiMicOff aria-hidden="true" />}
        {listening ? <><Meter />{accuracy != null && <span className={styles.score} aria-label={`Accuracy ${accuracy}%`}>{accuracy}%</span>}</> : session.mic === "starting" ? "Starting…" : "Listen"}
      </button>}
      <button className={styles.finish} onClick={() => { setOpen(false); practice.finish(); }}>Finish</button>
    </div>
    {calibrating && <Calibration onClose={() => setCalibrating(false)} />}
  </div>;
}
